import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Card, Skeleton } from '@/design-system/primitives'
import type { FloorTable, ServiceReport, WaiterRow } from '@/features/service/types'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { useAdminContext } from '../session'

/**
 * Le service en salle : qui sert quelles tables, et le bilan de la nuit.
 *
 * Deux moments, une page :
 *
 * * **avant et pendant** — chaque table a un serveur responsable, un seul à la
 *   fois ; un serveur en tient autant qu'on veut. Les commandes passées depuis
 *   une table vont dans son espace, à son nom. Réattribuer une table ne
 *   déplace pas les commandes déjà passées ;
 * * **après** — par serveur (ce qu'il a encaissé, remis, et ce qu'il a encore
 *   en poche), par poste (fond, ventes, apports, sorties, remises, attendu,
 *   compté) et les totaux qui doivent retomber les uns sur les autres.
 */
export function AdminServicePage() {
  const { t, i18n } = useTranslation()
  const { eventId, can } = useAdminContext()
  const canAssign = can('service.table.assign')
  const canReport = can('service.report.read')

  useEffect(() => {
    document.title = t('admin.section.service')
  }, [t])

  const base = `/admin/events/${eventId}/service`
  // L'attribution des tables n'est chargée — ni montrée — qu'à qui peut
  // attribuer. Le trésorier ouvre cet écran pour le bilan : lui présenter des
  // listes figées qu'il ne peut pas modifier ne fait que l'égarer.
  const tables = useAdminQuery<FloorTable[]>(
    ['admin-service-tables', eventId],
    `${base}/tables`,
    eventId,
    { enabled: canAssign },
  )
  const waiters = useAdminQuery<WaiterRow[]>(
    ['admin-service-waiters', eventId],
    `${base}/waiters`,
    eventId,
    { enabled: canAssign },
  )
  const report = useAdminQuery<ServiceReport>(
    ['admin-service-report', eventId],
    `${base}/report`,
    eventId,
    { enabled: canReport },
  )
  const assign = useAdminMutation<FloorTable, { table_id: string; waiter_id: string | null }>(
    'put',
    (variables) => `${base}/tables/${variables.table_id}/waiter`,
    eventId,
    [
      ['admin-service-tables', eventId],
      ['admin-service-waiters', eventId],
    ],
  )

  const currency = report.data?.currency ?? 'EUR'
  const money = (minor: number) => formatMoney({ amount_minor: minor, currency }, i18n.language)

  return (
    <>
      <AdminHeader title={t('admin.section.service')} description={t('admin.service.description')} />

      {/* ── Les tables et leur serveur ── */}
      {canAssign ? (
        <Card>
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.service.tablesTitle')}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-content-secondary">
            {t('admin.service.tablesHint')}
          </p>
          {waiters.data && waiters.data.length === 0 ? (
            <p className="mt-4 text-sm text-ember-300">{t('admin.service.noWaiters')}</p>
          ) : null}
          {tables.isPending ? <Skeleton className="mt-4 h-32 w-full" /> : null}
          {tables.data && tables.data.length === 0 ? (
            <p className="mt-4 text-sm text-content-muted">{t('admin.service.noTables')}</p>
          ) : null}
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(tables.data ?? []).map((table) => (
              <li
                key={table.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-line-subtle px-3 py-2"
              >
                <span className="min-w-0">
                  <span className="block truncate font-display text-heading-sm text-content-primary">
                    {table.label}
                  </span>
                  {table.status === 'out_of_service' ? (
                    <span className="text-caption text-content-muted">
                      {t('admin.service.outOfService')}
                    </span>
                  ) : null}
                </span>
                <select
                  value={table.waiter_id ?? ''}
                  disabled={!canAssign || assign.isPending}
                  aria-label={t('admin.service.waiterFor', { table: table.label })}
                  onChange={(event) =>
                    assign.mutate({ table_id: table.id, waiter_id: event.target.value || null })
                  }
                  className="h-11 max-w-[55%] rounded-md border border-line bg-bg-base px-2 text-sm text-content-primary"
                >
                  <option value="">{t('admin.service.unassigned')}</option>
                  {(waiters.data ?? []).map((waiter) => (
                    <option key={waiter.user_id} value={waiter.user_id}>
                      {waiter.name}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
          {assign.isError ? (
            <p className="mt-2 text-sm text-state-danger">{assign.error.message}</p>
          ) : null}

          {(waiters.data ?? []).length > 0 ? (
            <>
              <p className="mt-6 overline text-content-muted">{t('admin.service.byWaiter')}</p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {(waiters.data ?? []).map((waiter) => (
                  <li
                    key={waiter.user_id}
                    className="rounded-full border border-line-subtle px-3 py-1.5 text-sm text-content-secondary"
                  >
                    <span className="text-content-primary">{waiter.name}</span>
                    {' · '}
                    {waiter.tables.length
                      ? waiter.tables.map((table) => table.table_number).join(', ')
                      : t('admin.service.noTable')}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </Card>
      ) : null}

      {/* ── Le bilan ── */}
      {canReport ? (
        report.isPending ? (
          <Skeleton className="mt-8 h-64 w-full" />
        ) : report.data ? (
          <ServiceBilan report={report.data} money={money} />
        ) : null
      ) : null}
    </>
  )
}

function ServiceBilan({
  report,
  money,
}: {
  report: ServiceReport
  money: (minor: number) => string
}) {
  const { t } = useTranslation()
  const totals = report.totals

  return (
    <>
      <Card className="mt-8">
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.service.reportTitle')}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-content-secondary">
          {t('admin.service.reportHint')}
        </p>
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label={t('admin.service.totals.onsite')} value={money(totals.onsite_net_minor)} />
          <Stat label={t('admin.service.totals.tills')} value={money(totals.tills_net_minor)} />
          <Stat
            label={t('admin.service.totals.outside')}
            value={money(totals.outside_tills_net_minor)}
          />
          <Stat
            label={t('admin.service.totals.held')}
            value={money(totals.cash_held_by_staff_minor)}
            tone={totals.cash_held_by_staff_minor === 0 ? 'ok' : 'warn'}
          />
          <Stat label={t('admin.service.totals.handovers')} value={money(totals.handovers_minor)} />
          <Stat
            label={t('admin.service.totals.expected')}
            value={money(totals.expected_in_tills_minor)}
          />
          <Stat
            label={t('admin.service.totals.variance')}
            value={money(totals.variance_minor)}
            tone={totals.variance_minor === 0 ? 'ok' : 'warn'}
          />
          <Stat
            label={t('admin.service.totals.pending', { count: totals.pending_orders })}
            value={money(totals.pending_minor)}
            tone={totals.pending_orders === 0 ? 'ok' : 'warn'}
          />
        </div>
      </Card>

      <Card className="mt-6">
        <p className="overline text-content-muted">{t('admin.service.perWaiter')}</p>
        {report.waiters.length === 0 ? (
          <p className="mt-3 text-sm text-content-muted">{t('admin.service.nothingYet')}</p>
        ) : (
          <ul className="mt-3 grid gap-3 md:grid-cols-2">
            {report.waiters.map((row) => (
              <li key={row.user_id ?? 'none'} className="rounded-lg border border-line-subtle p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-display text-heading-sm text-content-primary">
                    {row.name || t('admin.service.noWaiterRow')}
                  </span>
                  <span
                    className={cn(
                      'text-sm tabular-nums',
                      row.cash_held_minor === 0 ? 'text-state-success' : 'text-ember-300',
                    )}
                  >
                    {t('admin.service.held', { amount: money(row.cash_held_minor) })}
                  </span>
                </div>
                <dl className="mt-3 space-y-1 text-sm">
                  <Line
                    label={t('admin.service.orders', {
                      paid: row.orders_paid,
                      pending: row.orders_pending,
                      cancelled: row.orders_cancelled,
                    })}
                    value={money(row.paid_minor)}
                  />
                  {row.orders_pending ? (
                    <Line label={t('admin.service.pendingAmount')} value={money(row.pending_minor)} />
                  ) : null}
                  <Line label={t('admin.service.cashReceived')} value={money(row.cash_received_minor)} />
                  <Line
                    label={t('admin.service.paypalConfirmed')}
                    value={money(row.paypal_confirmed_minor)}
                  />
                  {row.cash_refunded_minor ? (
                    <Line
                      label={t('admin.service.cashRefunded')}
                      value={money(-row.cash_refunded_minor)}
                    />
                  ) : null}
                  <Line label={t('admin.service.handedOver')} value={money(-row.handed_over_minor)} />
                  <Line label={t('admin.service.salesNet')} value={money(row.sales_net_minor)} />
                </dl>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="mt-6">
        <p className="overline text-content-muted">{t('admin.service.perPost')}</p>
        {report.posts.length === 0 ? (
          <p className="mt-3 text-sm text-content-muted">{t('admin.service.noTills')}</p>
        ) : (
          <ul className="mt-3 grid gap-3 md:grid-cols-3">
            {report.posts.map((post) => (
              <li key={post.station} className="rounded-lg border border-line-subtle p-4">
                <p className="font-display text-heading-sm text-content-primary">
                  {t(`admin.registers.station.${post.station}`, { defaultValue: post.station })}
                </p>
                <p className="text-caption text-content-muted">
                  {post.registers.join(', ')}
                  {post.open_sessions
                    ? ` · ${t('admin.cashSessions.openCount', { count: post.open_sessions })}`
                    : ''}
                </p>
                <dl className="mt-3 space-y-1 text-sm">
                  <Line label={t('pos.z.float')} value={money(post.opening_float_minor)} />
                  <Line label={t('pos.z.cashSales')} value={money(post.cash_sales_minor)} />
                  {post.refunds_minor ? (
                    <Line label={t('pos.z.refunds')} value={money(post.refunds_minor)} />
                  ) : null}
                  <Line label={t('pos.z.deposits')} value={money(post.deposits_minor)} />
                  <Line label={t('pos.z.payouts')} value={money(post.payouts_minor)} />
                  {post.corrections_minor ? (
                    <Line label={t('pos.z.corrections')} value={money(post.corrections_minor)} />
                  ) : null}
                  <Line label={t('pos.z.handovers')} value={money(post.handovers_minor)} />
                  <Line label={t('pos.expected')} value={money(post.expected_minor)} strong />
                  <Line label={t('pos.z.counted')} value={money(post.counted_minor)} />
                  <Line
                    label={t('pos.z.variance')}
                    value={money(post.variance_minor)}
                    tone={post.variance_minor === 0 ? 'ok' : 'warn'}
                  />
                  <Line label={t('pos.z.total')} value={money(post.revenue_minor)} />
                </dl>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-4 text-caption text-content-muted">{t('admin.service.tillsHint')}</p>
      </Card>
    </>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'ok' | 'warn'
}) {
  return (
    <div className="rounded-lg border border-line-subtle p-3">
      <p className="text-caption text-content-muted">{label}</p>
      <p
        className={cn(
          'mt-1 font-display text-heading-sm tabular-nums',
          tone === 'ok'
            ? 'text-state-success'
            : tone === 'warn'
              ? 'text-ember-300'
              : 'text-content-primary',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function Line({
  label,
  value,
  strong,
  tone,
}: {
  label: string
  value: string
  strong?: boolean
  tone?: 'ok' | 'warn'
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-content-secondary">{label}</dt>
      <dd
        className={cn(
          'tabular-nums',
          strong && 'font-medium',
          tone === 'ok'
            ? 'text-state-success'
            : tone === 'warn'
              ? 'text-ember-300'
              : 'text-content-primary',
        )}
      >
        {value}
      </dd>
    </div>
  )
}
